import React, { useMemo } from "react";
import { Entity } from "@common/types/entity";
import { getConsolidationGroupDisplayName } from "@common/helpers/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { Button } from "@design-system/Button/Button";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { HStack } from "@design-system/HStack/HStack";
import { CloseIcon } from "@icons";
import {
    FULL_OWNERSHIP_PERCENTAGE,
    getEntitySubtitle,
    getOwnershipEntityDisplayName,
    getOwnershipEntityIndustry,
    isActiveRelationship,
    RELATIONSHIP_TYPE_LABELS,
} from "../ownershipGraph.helpers";
import { OwnershipEntityIdentity } from "../OwnershipEntityIdentity";
import { OwnershipSelection } from "../ownership.types";
import { PaneSection } from "./PaneSection";
import styles from "./Pane.module.scss";

interface EntityPaneProps {
    entity: Entity;
    entitiesByUuid: Map<string, Entity>;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    onSelectionChange: (selection: OwnershipSelection) => void;
}

interface OwnershipRow {
    relationship: ConsolidationRelationshipResponse;
    counterparty: Entity | undefined;
}

function formatAddress(entity: Entity): string | null {
    if (!entity.address) {
        return null;
    }
    const { street, city, state } = entity.address;
    const parts = [street, city, state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
}

export const EntityPane: React.FC<EntityPaneProps> = ({
    entity,
    entitiesByUuid,
    relationships,
    groups,
    onSelectionChange,
}) => {
    const activeRelationships = useMemo(
        () => relationships.filter(isActiveRelationship),
        [relationships],
    );

    const ownerRows: OwnershipRow[] = useMemo(
        () =>
            activeRelationships
                .filter(
                    (relationship) =>
                        relationship.type ===
                            ConsolidationEntityRelationshipType.ownership &&
                        relationship.targetEntityUuid === entity.uuid,
                )
                .map((relationship) => ({
                    relationship,
                    counterparty: entitiesByUuid.get(
                        relationship.sourceEntityUuid,
                    ),
                })),
        [activeRelationships, entity.uuid, entitiesByUuid],
    );

    const subsidiaryRows: OwnershipRow[] = useMemo(
        () =>
            activeRelationships
                .filter(
                    (relationship) =>
                        relationship.type ===
                            ConsolidationEntityRelationshipType.ownership &&
                        relationship.sourceEntityUuid === entity.uuid,
                )
                .map((relationship) => ({
                    relationship,
                    counterparty: entitiesByUuid.get(
                        relationship.targetEntityUuid,
                    ),
                })),
        [activeRelationships, entity.uuid, entitiesByUuid],
    );

    const referenceRows: OwnershipRow[] = useMemo(
        () =>
            activeRelationships
                .filter(
                    (relationship) =>
                        relationship.type !==
                            ConsolidationEntityRelationshipType.ownership &&
                        (relationship.sourceEntityUuid === entity.uuid ||
                            relationship.targetEntityUuid === entity.uuid),
                )
                .map((relationship) => ({
                    relationship,
                    counterparty: entitiesByUuid.get(
                        relationship.sourceEntityUuid === entity.uuid
                            ? relationship.targetEntityUuid
                            : relationship.sourceEntityUuid,
                    ),
                })),
        [activeRelationships, entity.uuid, entitiesByUuid],
    );

    const group = useMemo(
        () =>
            groups.find(
                ({ parentEntityUuid, members }) =>
                    parentEntityUuid === entity.uuid ||
                    members.some(
                        ({ entityUuid }) => entityUuid === entity.uuid,
                    ),
            ) ?? null,
        [groups, entity.uuid],
    );

    const whollyOwnedRows = subsidiaryRows.filter(
        ({ relationship }) =>
            relationship.ownershipPercentage === FULL_OWNERSHIP_PERCENTAGE,
    );
    const partiallyOwnedRows = subsidiaryRows.filter(
        ({ relationship }) =>
            relationship.ownershipPercentage !== FULL_OWNERSHIP_PERCENTAGE,
    );

    const address = formatAddress(entity);
    const industry = getOwnershipEntityIndustry(entity);

    const renderOwnershipRow = ({
        relationship,
        counterparty,
    }: OwnershipRow) => (
        <Button
            key={relationship.id}
            variant="secondary"
            size="md"
            block
            className={styles.linkRow}
            onClick={() =>
                onSelectionChange({
                    kind: "relationship",
                    relationshipId: relationship.id,
                })
            }
            leftIcon={
                counterparty && (
                    <EntityAvatar entity={counterparty} size="xs" aria-hidden />
                )
            }
            rightIcon={
                relationship.ownershipPercentage !== null && (
                    <span className={styles.linkRowValue}>
                        {`${relationship.ownershipPercentage}%`}
                    </span>
                )
            }
        >
            <span className={styles.linkRowLabel}>
                <BreakableText
                    text={
                        counterparty
                            ? getOwnershipEntityDisplayName(counterparty)
                            : "Unknown entity"
                    }
                    showTooltip
                />
            </span>
        </Button>
    );

    return (
        <div className={styles.pane}>
            <HStack
                align="center"
                justify="between"
                className={styles.entityHeader}
            >
                <OwnershipEntityIdentity entity={entity} />
                <Button
                    variant="secondary"
                    size="md"
                    iconOnly
                    onClick={() => onSelectionChange(null)}
                    aria-label="Close entity details"
                >
                    <CloseIcon />
                </Button>
            </HStack>
            <PaneSection title="Details" defaultExpanded>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Name</span>
                    <span className={styles.detailValue}>
                        {getOwnershipEntityDisplayName(entity)}
                    </span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Entity type</span>
                    <span className={styles.detailValue}>
                        {getEntitySubtitle(entity)}
                    </span>
                </div>
                {industry && (
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Industry</span>
                        <span className={styles.detailValue}>{industry}</span>
                    </div>
                )}
                {address && (
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Address</span>
                        <span className={styles.detailValue}>{address}</span>
                    </div>
                )}
                {group && (
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                            Consolidation
                        </span>
                        <span className={styles.detailValue}>
                            {group.parentEntityUuid === entity.uuid
                                ? `Parent of ${getConsolidationGroupDisplayName({ groupName: group.name, parentEntityName: entity.name })}`
                                : `Subsidiary of ${getConsolidationGroupDisplayName({ groupName: group.name, parentEntityName: entitiesByUuid.get(group.parentEntityUuid)?.name })}`}
                        </span>
                    </div>
                )}
            </PaneSection>

            <PaneSection title="Owners">
                {ownerRows.length === 0 && (
                    <div className={styles.emptyHint}>No owners recorded</div>
                )}
                {ownerRows.map(renderOwnershipRow)}
            </PaneSection>

            <PaneSection title="Subsidiaries">
                {subsidiaryRows.length === 0 && (
                    <div className={styles.emptyHint}>No subsidiaries</div>
                )}
                {whollyOwnedRows.length > 0 && (
                    <div className={styles.groupLabel}>Wholly owned</div>
                )}
                {whollyOwnedRows.map(renderOwnershipRow)}
                {partiallyOwnedRows.length > 0 && (
                    <div className={styles.groupLabel}>Partially owned</div>
                )}
                {partiallyOwnedRows.map(renderOwnershipRow)}
            </PaneSection>

            <PaneSection title="Relationships">
                {referenceRows.length === 0 && (
                    <div className={styles.emptyHint}>
                        No other relationships
                    </div>
                )}
                {referenceRows.map(({ relationship, counterparty }) => (
                    <Button
                        key={relationship.id}
                        variant="secondary"
                        size="md"
                        block
                        className={styles.linkRow}
                        onClick={() =>
                            onSelectionChange({
                                kind: "relationship",
                                relationshipId: relationship.id,
                            })
                        }
                        leftIcon={
                            counterparty && (
                                <EntityAvatar
                                    entity={counterparty}
                                    size="xs"
                                    aria-hidden
                                />
                            )
                        }
                        rightIcon={
                            <span className={styles.linkRowValue}>
                                {relationship.metadata?.name ??
                                    RELATIONSHIP_TYPE_LABELS[relationship.type]}
                            </span>
                        }
                    >
                        <span className={styles.linkRowLabel}>
                            <BreakableText
                                text={
                                    counterparty
                                        ? getOwnershipEntityDisplayName(
                                              counterparty,
                                          )
                                        : "Unknown entity"
                                }
                                showTooltip
                            />
                        </span>
                    </Button>
                ))}
            </PaneSection>
        </div>
    );
};
